import { Key, ReactNode, useState } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import { alpha, keyframes, SxProps, Theme } from '@mui/material/styles';
import { isEqual } from 'lodash';

/**
 * `flash` marks a discrete change (status, versions, manifest fields, component rows);
 * `pulse` is the quieter variant for a value that changes on a heartbeat (`last_online`).
 */
export type LiveHighlightVariant = 'flash' | 'pulse';

/** Identity a highlight baseline belongs to — in practice the record id. */
export type LiveHighlightResetKey = Key | null | undefined;

/**
 * Which of a variant's two identical keyframe copies a change uses: `a` on odd generations, `b` on
 * even ones. Moving to a different `animation-name` restarts the animation on the same element, so
 * consecutive changes alternate copies and nothing has to be remounted.
 */
type LiveHighlightPhase = 'a' | 'b';

// Both variants animate `background-color` towards transparent, never layout properties, so a
// running highlight cannot shift anything. The colour is read from `--live-highlight-color`, set
// from the theme by `liveHighlightSx`, which keeps the keyframes themselves theme-agnostic.
//
// Emotion names keyframes after a hash of their text, so two identical copies would collapse into
// one name and the restart would never happen. The `label` sets the copies apart: it is appended to
// the generated name (`animation-<hash>-live-highlight-flash-a`) and stripped from the emitted CSS.
const flashKeyframes = (phase: LiveHighlightPhase) => keyframes`
  label: live-highlight-flash-${phase};
  0%,
  20% {
    background-color: var(--live-highlight-color);
  }
  100% {
    background-color: transparent;
  }
`;

const pulseKeyframes = (phase: LiveHighlightPhase) => keyframes`
  label: live-highlight-pulse-${phase};
  0% {
    background-color: transparent;
  }
  40% {
    background-color: var(--live-highlight-color);
  }
  100% {
    background-color: transparent;
  }
`;

type Keyframes = ReturnType<typeof keyframes>;

const VARIANTS: Record<
  LiveHighlightVariant,
  { frames: Record<LiveHighlightPhase, Keyframes>; strength: number; durationMs: number; easing: string }
> = {
  flash: {
    frames: { a: flashKeyframes('a'), b: flashKeyframes('b') },
    strength: 0.45,
    durationMs: 1000,
    easing: 'ease-out',
  },
  pulse: {
    frames: { a: pulseKeyframes('a'), b: pulseKeyframes('b') },
    strength: 0.2,
    durationMs: 1600,
    easing: 'ease-in-out',
  },
};

const highlightStyles = (theme: Theme, variant: LiveHighlightVariant, generation: number) => {
  const { frames, strength, durationMs, easing } = VARIANTS[variant];
  const phase: LiveHighlightPhase = generation % 2 === 1 ? 'a' : 'b';

  return {
    '--live-highlight-color': alpha(theme.palette.primary.main, strength),
    'animation': `${frames[phase]} ${durationMs}ms ${easing}`,
  };
};

/**
 * The highlight animation for `variant` at change `generation` (from `useLiveHighlight`), for an
 * element that animates its own background — a table row, where `LiveHighlight`'s overlay cannot
 * go. Consecutive generations use alternating keyframe names, so each change restarts the
 * animation on the same element: do not key the element by `generation`, which would remount it
 * and its children (dropping text selection and moving a screen-reader cursor).
 */
export const liveHighlightSx =
  (variant: LiveHighlightVariant, generation: number): SxProps<Theme> =>
  (theme: Theme) =>
    highlightStyles(theme, variant, generation);

/**
 * Tracks changes of `value` and reports whether a highlight should show.
 *
 * - `generation` is 0 on mount and after a reset, and goes up by one on every later change. Pass it
 *   to `liveHighlightSx`, which alternates keyframe names by it, so a change inside a running
 *   animation restarts it on the same element instead of stacking a second one.
 * - `active` is true once the value has changed at least once since mount or reset, unless the
 *   user prefers reduced motion — then nothing animates, and the value itself still updates.
 * - A change of `resetKey` (a unit switch) moves the baseline to the new value without counting
 *   it as a change.
 *
 * `value` is compared deeply, so an equal object re-created by a cache merge or a refetch does not
 * count as a change. The previous value is held in state and compared during render — React's
 * "storing information from previous renders" pattern — so there is no effect and no extra commit.
 */
export const useLiveHighlight = (value: unknown, resetKey: LiveHighlightResetKey) => {
  const [baseline, setBaseline] = useState({ value, resetKey, generation: 0 });
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)', { noSsr: true });

  let current = baseline;
  if (!Object.is(baseline.resetKey, resetKey)) {
    current = { value, resetKey, generation: 0 };
  } else if (!isEqual(baseline.value, value)) {
    current = { value, resetKey, generation: baseline.generation + 1 };
  }
  // A plain value rather than an updater: StrictMode's second render computes the same object.
  if (current !== baseline) {
    setBaseline(current);
  }

  return { generation: current.generation, active: current.generation > 0 && !prefersReducedMotion };
};

export interface LiveHighlightProps {
  /** The watched value. Only a change of this after mount animates. */
  value: unknown;
  /** The record id, or any identity whose change must reset the baseline without animating. */
  resetKey: LiveHighlightResetKey;
  variant?: LiveHighlightVariant;
  children: ReactNode;
}

/**
 * Briefly highlights its children when `value` changes after mount — never on the first render,
 * never on a `resetKey` change, never under `prefers-reduced-motion: reduce`.
 *
 * The highlight is an absolutely positioned overlay behind the children (`aria-hidden`, marked
 * `data-live-highlight="<variant>"`), so the children are never remounted and nothing moves. The
 * wrapper shrinks to its content, so the highlight hugs the field rather than the whole grid cell.
 *
 * The overlay is keyed by `generation` as well as alternating its keyframe names: it is an empty
 * decorative span, so replacing it costs nothing, and a fresh node is the most direct restart.
 */
export const LiveHighlight = ({ value, resetKey, variant = 'flash', children }: LiveHighlightProps) => {
  const { generation, active } = useLiveHighlight(value, resetKey);

  return (
    <Box sx={{ position: 'relative', isolation: 'isolate', width: 'fit-content', maxWidth: '100%' }}>
      {children}
      {active && (
        <Box
          key={generation}
          component="span"
          aria-hidden
          data-live-highlight={variant}
          sx={(theme: Theme) => ({
            position: 'absolute',
            inset: theme.spacing(-0.5, -1),
            zIndex: -1,
            borderRadius: 1,
            pointerEvents: 'none',
            ...highlightStyles(theme, variant, generation),
          })}
        />
      )}
    </Box>
  );
};
